import type {
  CreateWebhookDTO,
  IWebhookRepository,
  PaginatedResponse,
  UpdateWebhookDTO,
  Webhook,
  WebhookDelivery,
  WebhookTestResponse,
} from '@yawi/domain';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiWebhookResponse {
  id: string;
  url: string;
  name?: string | null;
  description?: string | null;
  events: string[];
  status: 'ACTIVE' | 'INACTIVE' | 'FAILED';
  failureCount: number;
  lastTriggeredAt?: string | null;
  lastSuccessAt?: string | null;
  lastFailureAt?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiWebhookDeliveryResponse {
  id: string;
  webhookId: string;
  event: string;
  payload: Record<string, unknown>;
  status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'RETRYING';
  responseStatus?: number | null;
  attemptCount: number;
  deliveredAt?: string | null;
  createdAt: string;
}

interface ApiWebhookTestResponse {
  success: boolean;
  message: string;
}

// Mapea la respuesta de la API al tipo Webhook interno
const mapApiWebhookToWebhook = (apiWebhook: ApiWebhookResponse): Webhook => ({
  id: apiWebhook.id,
  url: apiWebhook.url,
  name: apiWebhook.name || undefined,
  description: apiWebhook.description || undefined,
  events: apiWebhook.events as Webhook['events'],
  status: apiWebhook.status,
  failureCount: apiWebhook.failureCount,
  lastTriggeredAt: apiWebhook.lastTriggeredAt || undefined,
  lastSuccessAt: apiWebhook.lastSuccessAt || undefined,
  lastFailureAt: apiWebhook.lastFailureAt || undefined,
  createdBy: apiWebhook.createdBy,
  createdAt: apiWebhook.createdAt,
  updatedAt: apiWebhook.updatedAt,
});

// Mapea la respuesta de la API al tipo WebhookDelivery interno
const mapApiDeliveryToDelivery = (apiDelivery: ApiWebhookDeliveryResponse): WebhookDelivery => ({
  id: apiDelivery.id,
  webhookId: apiDelivery.webhookId,
  event: apiDelivery.event as WebhookDelivery['event'],
  payload: apiDelivery.payload,
  status: apiDelivery.status,
  responseStatus: apiDelivery.responseStatus || undefined,
  attemptCount: apiDelivery.attemptCount,
  deliveredAt: apiDelivery.deliveredAt || undefined,
  createdAt: apiDelivery.createdAt,
});

export class ApiWebhookRepository implements IWebhookRepository {
  async getAllWebhooks(): Promise<Webhook[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiWebhookResponse>>('v1/webhooks');
    return response.data.map(mapApiWebhookToWebhook);
  }

  async getMyWebhooks(): Promise<Webhook[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiWebhookResponse>>('v1/webhooks/my');
    return response.data.map(mapApiWebhookToWebhook);
  }

  async getWebhookById(id: string): Promise<Webhook | null> {
    const client = getApiClient();
    try {
      const webhook = await client.get<ApiWebhookResponse>(`v1/webhooks/${id}`);
      return mapApiWebhookToWebhook(webhook);
    } catch {
      return null;
    }
  }

  async createWebhook(webhook: CreateWebhookDTO): Promise<Webhook> {
    const client = getApiClient();
    const apiWebhook = await client.post<ApiWebhookResponse>('v1/webhooks', webhook);
    return mapApiWebhookToWebhook(apiWebhook);
  }

  async updateWebhook(id: string, webhook: UpdateWebhookDTO): Promise<Webhook> {
    const client = getApiClient();
    const apiWebhook = await client.put<ApiWebhookResponse>(`v1/webhooks/${id}`, webhook);
    return mapApiWebhookToWebhook(apiWebhook);
  }

  async deleteWebhook(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/webhooks/${id}`);
  }

  async getDeliveries(webhookId: string): Promise<WebhookDelivery[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiWebhookDeliveryResponse>>(`v1/webhooks/${webhookId}/deliveries`);
    return response.data.map(mapApiDeliveryToDelivery);
  }

  async testWebhook(id: string): Promise<WebhookTestResponse> {
    const client = getApiClient();
    return await client.post<ApiWebhookTestResponse>(`v1/webhooks/${id}/test`);
  }
}

